import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as actions from './../../data/settings/actions';
import MainCardContainer from '../../components/MainCardContainer/';
import Notification from '../../components/Notification';
import AlertDialog from '../../components/AlertDialog';
import {
  BID_TYPES,
  CATEGORY_ID,
  httpStatus,
  LANGUAGE,
  STDBIDSESSION_ID,
  STAFFID,
  RESERVE_DOWN_TO
} from './../../data/constants/';
import Loader from './../../components/Loader';
import BidTable from './../BidSession/components/BidTable';
import OtherOptions from './../BidSession/components/OtherOptions';
import CardRow from './../../components/CardRow';
import {
  getBidMode,
  bidSessionUpdateWeight,
  movePreference,
  orderPreferences,
  requestWeightSorting,
  moveItem, sortPreferences, processPreferences, getLastBidScopeUsed, getStaffId,
  isOptionLevelIsSmallerEqualThanReserve
} from './../BidSession/helpers';
import * as actionsBidSession from './../../data/bidSession/actions';
import * as linearCalendarActions from '../../data/linearCalendar/actions';
import { updateBidSessionSuccess } from "../../data/bidSession/productionActions";
import { copyCombinedPreference } from '../../data/bidClasses/productionActions';
import { useLanguage } from '../../context/LanguageContext';

const DefaultSettings = (props) => {
  // Ref for BidTable to allow calling checkBidSessionRequests
  const bidTableRef = useRef();
  const { labels } = useLanguage();
  const BID_TABLE = labels.BID_TABLE || {};
  const RESERVE_LINE = labels.RESERVE_LINE || {};
  const otherOptionsLabels = labels.otherOptions || {};
  const ALERT_DIALOG = labels.ALERT_DIALOG || {};
  const [state, setState] = useState({
    lastSave: null,
    profileLastSave: null,
    bidPreferences: { entries: [] },
    preferenceAdded: false,
    bidmode: '',
    openbid: [],
    reserveLine: {},
    updatedReserveLineType: [],
    isReserveLineTypeSaveFail: false,
    configureReserveLineType: [],
    statusDisplay: false,
    statusDisplayText: '',
    alertDialogDisplay: false,
    alertDialogDisplayText: '',
    loadingBidTable: false
  });

  // Refs for persistent values
  const bidSessionRef = useRef({
    currentBid: {
      bidSessionId: sessionStorage.getItem(STDBIDSESSION_ID)
    },
    user: {}
  });
  const preferenceHasChangeRef = useRef(false);
  const reserveLineMessageShownRef = useRef(false);

  // Set session storage flag
  useEffect(() => {
    sessionStorage.setItem('islastViewedDefault', true);
  }, []);

  // Equivalent to UNSAFE_componentWillMount
  useEffect(() => {
    const staffId = getStaffId();
    const standingBidSessionId = sessionStorage.getItem(STDBIDSESSION_ID);
    const { fetchBidSession } = props;
    const defaultBid = true;
    fetchBidSession(staffId, standingBidSessionId, undefined, defaultBid);
  }, []);

  // Equivalent to componentDidMount
  useEffect(() => {
    const staffId = sessionStorage.getItem(STAFFID);
    const standingBidSessionId = sessionStorage.getItem(STDBIDSESSION_ID);
    const categoryId = sessionStorage.getItem(CATEGORY_ID);
    const { fetchSettings, openbid, getUserInfo } = props;

    fetchSettings(staffId, standingBidSessionId);

    openbid(categoryId).then(response => {
      setState(prev => ({
        ...prev,
        openbid: response.data.entries
      }));
    });

    const lang = sessionStorage.getItem(LANGUAGE.LANG_KEY);
    if (lang) {
      setState(prev => ({
        ...prev,
        value: lang === LANGUAGE.DEFAULT_LANG ? 1 : 2
      }));
    }

    getUserInfo(staffId).then(response => {
      setState(prev => ({
        ...prev,
        profileLastSave: response.data.profileLastSave
      }));
      actions.reloadSessionStorageUserInfo(response.data);
    });
  }, []);

  // Equivalent to UNSAFE_componentWillReceiveProps
  useEffect(() => {
    const { settings, user, isSettingsFetching, bidSession } = props;

    if (bidSession?.reserveLine?.message != null && !reserveLineMessageShownRef.current) {
      showReserveLineAlertDialog(bidSession?.reserveLine?.message);
      reserveLineMessageShownRef.current = true;
    }

    const lastRevisionData = settings.lastRevisionData || {};
    const lastSave = lastRevisionData.lastSave;
    let bidPreferences = settings.bidPreferencesData || {};

    if (bidSession.bidPreferences) {
      bidPreferences = bidSession.bidPreferences;
    }

    if (bidPreferences.totalEntries) {
      bidPreferences.entries = orderPreferences(bidPreferences.entries);
    }

    if (!isSettingsFetching) {
      setState(prev => ({
        ...prev,
        lastSave,
        bidPreferences,
        reserveLine: bidSession?.reserveLine
      }));
      bidSessionRef.current.user = user;
    }
  }, [props.settings, props.user, props.isSettingsFetching, props.bidSession]);

  // Equivalent to componentDidUpdate
  useEffect(() => {
    // Scroll to bottom the bid preferences list when a new preference is added
    if (state.preferenceAdded) {
      const target = document.getElementsByClassName('rows-container')[0];
      if (target) {
        target.scrollTop = target.scrollHeight;
      }
    }
  }, [state.preferenceAdded]);

  // Equivalent to componentWillUnmount
  useEffect(() => {
    return () => {
      props.clearData();
      bidSessionRef.current = {};
    };
  }, []);

  const handleActionPreference = useCallback((response, newPreferenceAdded = false) => {
    if (response && response.status === httpStatus.OK) {
      const bidPreferences = orderPreferences(response.data.preferences);
      const lastSave = response.data.revision.lastSave;
      setState(prev => ({
        ...prev,
        preferenceAdded: newPreferenceAdded,
        lastSave,
        bidPreferences: {
          start: 0,
          end: bidPreferences.length - 1,
          totalEntries: bidPreferences.length,
          entries: bidPreferences
        }
      }));
    }
  }, []);

  const reorderreserveline = useCallback((data) => {
    const { reorderReserveline } = props;
    const staffId = getStaffId();
    const standingBidSessionId = sessionStorage.getItem(STDBIDSESSION_ID);
    const categoryId = sessionStorage.getItem(CATEGORY_ID);
    reorderReserveline(staffId, standingBidSessionId, categoryId, data).then(response => {
      if (response && response.status === httpStatus.OK) {
        setState(prev => ({
          ...prev,
          isReserveLineTypeSaveFail: false,
          updatedReserveLineType: response.data.bidderReserveLines
        }));
        showNotification(RESERVE_LINE.NOTIFICATION.SUCCESS);
        // Call checkBidSessionRequests on BidTable after successful reorder
        if (bidTableRef.current && typeof bidTableRef.current.checkBidSessionRequests === 'function') {
          bidTableRef.current.checkBidSessionRequests();
        }
      } else {
        setState(prev => ({
          ...prev,
          isReserveLineTypeSaveFail: true,
          updatedReserveLineType: [...state.reserveLine?.bidderReserveLines]
        }));
        showNotification(response?.data?.httpStatus);
      }
    })
      .catch(e => {
        setState(prev => ({
          ...prev,
          isReserveLineTypeSaveFail: true,
          updatedReserveLineType: [...state.reserveLine?.bidderReserveLines]
        }));
        showNotification(e?.statusText || e?.response?.statusText);
      });
  }, [state.reserveLine?.bidderReserveLines, props]);

  const handleMovePreference = useCallback((type, oldScore, isCombined) => {
    const preferencesToCheck = processPreferences(
      type,
      oldScore,
      state.bidPreferences.entries,
      showNotification
    );

    if (!preferencesToCheck) return;

    setState(prev => ({ ...prev, loadingBidTable: true }));
    const { switchPriority } = props;
    const { bidAggregateIds, bidAggregateIdsToSwitchWith } = movePreference(
      type,
      oldScore,
      preferencesToCheck,
      isCombined
    );

    switchPriority(bidAggregateIds, bidAggregateIdsToSwitchWith)
      .then(response => {
        if (response && response.status === httpStatus.OK) {
          const bidPreferences = sortPreferences(response.data.preferences);
          preferenceHasChangeRef.current = !preferenceHasChangeRef.current;
          setState(prev => ({
            ...prev,
            loadingBidTable: false,
            bidPreferences: {
              start: 0,
              end: bidPreferences.length - 1,
              totalEntries: bidPreferences.length,
              entries: bidPreferences
            }
          }));
        }
      })
      .catch(e => console.log(e.statusText));
  }, [state.bidPreferences.entries, props]);

  const copyPreference = useCallback((employeeBidId, groupName) => {
    const { fetchBidSession, copyCombinedPreference } = props;
    setState(prev => ({ ...prev, loadingBidTable: true }));
    
    copyCombinedPreference(employeeBidId, groupName)
      .then(response => {
        if (response.data.errorMessages && response.status !== httpStatus.OK) {
          setState(prev => ({
            ...prev,
            loadingBidTable: false,
            isDisplayedErrorMessage: true
          }));
          const newArr = Object.values(response.data.errorMessages);
          const mappedArr = newArr.map(i => i);
          showNotification(mappedArr);
          return;
        } else {
          const bidPreferences = response?.data?.preferences;
          if (bidPreferences) {
            setState(prev => ({
              ...prev,
              loadingBidTable: false,
              bidPreferences: {
                start: 0,
                end: bidPreferences.length - 1,
                totalEntries: bidPreferences.length,
                entries: bidPreferences
              },
              isDisplayedErrorMessage: false
            }));
          }
          const staffId = getStaffId();
          const standingBidSessionId = sessionStorage.getItem(STDBIDSESSION_ID);
          const defaultBid = true;
          fetchBidSession(staffId, standingBidSessionId, undefined, defaultBid);
        }
      })
      .catch(error => {
        setState(prev => ({ ...prev, loadingBidTable: false }));
        console.error(error);
      });
  }, [props, setState]);

  const reorderPreferences = useCallback((oldIndex, newIndex) => {
    // get the bid scope to filter the preferences to move
    let lastBidScopeUsed = getLastBidScopeUsed();
    let fixedPreferences = state.bidPreferences.entries.filter(
      (preference) => isOptionLevelIsSmallerEqualThanReserve(preference.optionLevel)
    );
    let preferencesToCheck = state.bidPreferences.entries.filter(
      preference => preference.preferenceOptionPropertyId === lastBidScopeUsed && preference.optionLevel > RESERVE_DOWN_TO.levelAny
    );
    // Adjust indexes for preferencesToCheck
    let preferencesInScope = state.bidPreferences.entries.filter(preference => preference.preferenceOptionPropertyId === lastBidScopeUsed);
    let preferencesNotInScope = state.bidPreferences.entries.filter(preference => preference.preferenceOptionPropertyId !== lastBidScopeUsed);

    const validIndexes = preferencesInScope.reduce((acc, preference, index) => {
      if (preferencesToCheck.includes(preference)) acc.push(index);
      return acc;
    }, []);

    const adjustedOldIndex = validIndexes.indexOf(oldIndex);
    const adjustedNewIndex = validIndexes.indexOf(newIndex);

    if (adjustedOldIndex === -1 || adjustedNewIndex === -1) return;
    // Move only allowed preferences
    let updatePrefs = moveItem(preferencesToCheck, adjustedOldIndex, adjustedNewIndex);
    setState(prev => ({
      ...prev,
      bidPreferences: { entries: [...fixedPreferences, ...updatePrefs, ...preferencesNotInScope] }
    }));

    const prevState = { ...state };
    const { updateAllScores } = props;
    setState(prev => ({
      ...prev,
      readOnly: true,
      loadingBidTable: true
    }));

    updateAllScores(updatePrefs)
      .then(response => {
        if (response && response.status === httpStatus.OK) {
          preferenceHasChangeRef.current = !preferenceHasChangeRef.current;
          setState(prev => ({ ...prev, loadingBidTable: false }));
        }
      })
      .catch(() => {
        setState(prevState);
      });
  }, [state, props]);

  const weightSorting = useCallback((sort) => {
    const componentContext = {
      state,
      setState,
      props,
      bidSession: bidSessionRef.current,
      preferenceHasChangeRef,
      updateWeight: props.updateWeight
    };
    requestWeightSorting(componentContext, sort);
  }, [state, props]);

  const handleWeightEdit = useCallback((preference, weight, bidmode) => {
    const { bidPreferences } = state;
    const bidId = preference.bidAggregateId;
    const componentContext = {
      state,
      setState,
      props,
      bidSession: bidSessionRef.current,
      preferenceHasChangeRef,
      updateWeight: props.updateWeight
    };
    bidSessionUpdateWeight(
      componentContext,
      bidPreferences,
      bidId,
      weight,
      preference,
      bidmode
    );
  }, [state, props]);

  const showNotification = useCallback((message) => {
    setState(prev => ({
      ...prev,
      statusDisplay: true,
      statusDisplayText: message
    }));
  }, []);

  const closeStatusDisplay = useCallback(() => {
    setState(prev => ({
      ...prev,
      statusDisplay: false,
      statusDisplayText: ''
    }));
  }, []);

  const showReserveLineAlertDialog = useCallback((message) => {
    setState(prev => ({
      ...prev,
      alertDialogDisplay: true,
      alertDialogDisplayText: message
    }));
  }, []);

  const closeReserveLineAlertDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      alertDialogDisplay: false,
      alertDialogDisplayText: ''
    }));
  }, []);

  // Render function converted to return statement
  const { isSettingsFetching, isFetching, updateOtherOption, fetchActivities, bidSession: propsBidSession } = props;
  let { bidSession } = props;
  let bidMode = bidSession.bidMode;
  bidSession = { ...bidSession, currentBid: { ...bidSession.currentBid, bidSessionId: sessionStorage.getItem(STDBIDSESSION_ID) } }

  // Set up variables needed for OtherOptions similar to BidSession
  const user = bidSession.user || {};
  const currentCategory = user.currentCategory || {};
  const categoryId = currentCategory.categoryId;
  const otherOptions = bidSession.otherOptions
    ? bidSession.otherOptions
    : { entries: [] };
  const staffId = getStaffId();

  let calendarStartDateUTCStr = null;
  let calendarEndDateUTCString = null;

  const otherOptionSaveBody = {
    staffId,
    bidSessionId: sessionStorage.getItem(STDBIDSESSION_ID),
    categoryId,
    startDateSession: null,
    endDateSession: null,
    value: null
  };

  if (
    bidSession.bidPreferences &&
    bidSession.bidPreferences.entries &&
    bidSession.bidPreferences.entries.length > 0
  ) {
    bidMode = bidSession.bidPreferences.entries[0].bidMode;
  }
  if (((state.updatedReserveLineType && state.updatedReserveLineType.length !== 0) &&
    JSON.stringify(bidSession?.reserveLine?.bidderReserveLines) !== JSON.stringify(state.updatedReserveLineType))
    || (state.isReserveLineTypeSaveFail && (state.updatedReserveLineType && state.updatedReserveLineType.length !== 0))) {

    bidSession = {
      ...bidSession, reserveLine: { ...bidSession.reserveLine, bidderReserveLines: state.updatedReserveLineType }
    }
  }

  // Handler to call checkBidSessionRequests after waiver toggle
  const handleOtherOptionToggled = () => {
    if (bidTableRef.current && typeof bidTableRef.current.checkBidSessionRequests === 'function') {
      bidTableRef.current.checkBidSessionRequests();
    }
  };

  return (
    <div>
      {isSettingsFetching || isFetching || !propsBidSession?.configureReserveLineType ? (
        <Loader />
      ) : (
        <MainCardContainer
          headTitle={BID_TABLE.STANDING_BIDS.SESSION_NAME}
          headSubtitle={''}
          content={[
            <CardRow
              key={0}
              cardNumber={1}
              cards={[
                <BidTable
                  key={0}
                  ref={bidTableRef}
                  bidSession={{
                    ...bidSession,
                    bidPreferences: state.bidPreferences,
                    filteredBidPreferences: JSON.parse(JSON.stringify(state.bidPreferences)),
                    bidMode: bidMode || getBidMode()
                  }}
                  handleActionPreference={handleActionPreference}
                  handleMovePreference={handleMovePreference}
                  title={BID_TABLE.STANDING_BIDS.LIST_TITLE}
                  subtitle={BID_TABLE.STANDING_BIDS.CARD_SUBTITLE}
                  listTitle={BID_TABLE.STANDING_BIDS.LIST_TITLE}
                  optionPropertyId={BID_TYPES.DEFAULT}
                  handleWeightEdit={handleWeightEdit}
                  weightSorting={weightSorting}
                  reorderPreferences={reorderPreferences}
                  updateWeight={props.updateWeight}
                  updateWeightAll={props.updateWeightAll}
                  updateBidSession={props.updateBidSession}
                  reorderReserveline={reorderreserveline}
                  isReserveLineTypeSaveFailed={state.isReserveLineTypeSaveFail}
                  loadingBidTable={state.loadingBidTable}
                  defaultSettings={true}
                  copyCombinedPreference={copyPreference}
                />
              ]}
            />,
            <CardRow
              key={1}
              cardNumber={1}
              cards={[
                <OtherOptions
                  key={0}
                  otherOptions={otherOptions}
                  updateOtherOption={updateOtherOption}
                  otherOptionSaveBody={otherOptionSaveBody}
                  showNotification={showNotification}
                  fetchActivities={fetchActivities}
                  calendarStartDateStr={calendarStartDateUTCStr}
                  calendarEndDateStr={calendarEndDateUTCString}
                  onOptionToggled={handleOtherOptionToggled}
                  title={otherOptionsLabels?.STANDING_OPTIONS?.TITLE || 'Options'}
                  subtitle={otherOptionsLabels?.STANDING_OPTIONS?.SUBTITLE || ''}
                />
              ]}
            />
          ]}
        />
      )}
      <Notification
        open={state.statusDisplay}
        message={state.statusDisplayText}
        closeStatusDisplay={closeStatusDisplay}
      />
      <AlertDialog
        openAlertDialog={state.alertDialogDisplay}
        alertDialogMessage={state.alertDialogDisplayText}
        handleOk={closeReserveLineAlertDialog}
        handleCancel={closeReserveLineAlertDialog}
        okButtonLabel={ALERT_DIALOG.OK}
        cancelButtonLabel={null}
      />
    </div>
  );
};

DefaultSettings.defaultProps = {
  bidSession: {
    user: {},
    bidPreferences: {}
  },
  isSettingsFetching: false,
  settings: {},
  user: {},
  error: {},
  fetchBidSession: () => {},
  fetchSettings: () => {},
  switchPriority: () => {},
  updateScore: () => {},
  updateWeight: () => {},
  updateWeightMode: () => {},
  getUserInfo: () => {},
  openbid: () => {},
  updateWeightAll: () => {},
  updateBidSession: () => {},
  reorderReserveline: () => {},
  updateOtherOption: () => {},
  fetchActivities: () => {}
};

DefaultSettings.propTypes = {
  bidSession: PropTypes.object.isRequired,
  isSettingsFetching: PropTypes.bool,
  settings: PropTypes.object,
  user: PropTypes.object,
  fetchBidSession: PropTypes.func,
  fetchSettings: PropTypes.func,
  getUserInfo: PropTypes.func,
  switchPriority: PropTypes.func,
  updateScore: PropTypes.func,
  updateWeightMode: PropTypes.func,
  updateWeight: PropTypes.func,
  openbid: PropTypes.func,
  updateBidSession: PropTypes.func.isRequired,
  updateWeightAll: PropTypes.func,
  reorderReserveline: PropTypes.func,
  copyCombinedPreference: PropTypes.func,
  updateOtherOption: PropTypes.func,
  fetchActivities: PropTypes.func
};


const mapStateToProps = state => {
  return ({ ...state.bidSession,...state.settings, ...state.login })};

const mapDispatchToProps = dispatch => ({
  clearData: () => dispatch(actionsBidSession.default.clearData()),
  fetchBidSession: (staffId, bidId, applyStandingBid, defaultBid) =>
    dispatch(
      actionsBidSession.default.fetchBidSession(
        staffId,
        bidId,
        applyStandingBid,
        defaultBid
      )
    ),
  fetchSettings: (staffId, standingBidSessionId) =>
    dispatch(actions.fetchSettings(staffId, standingBidSessionId)),
  switchPriority: (type, oldScore, bidSession) =>
    dispatch(
      actionsBidSession.default.switchPriority(type, oldScore, bidSession)
    ),
  updateScore: (preference, newScore) =>
    dispatch(actionsBidSession.default.updateScore(preference, newScore)),
  getUserInfo: staffId => dispatch(actions.getUserInfo(staffId)),

  reorderReserveline:(staffId,standingBidSessionId,categoryId,reorderedReserveline)=>
    dispatch(actionsBidSession.default.reorderReserveline(staffId,standingBidSessionId,categoryId,reorderedReserveline)),

  openbid: categoryId => dispatch(actions.openbid(categoryId)),
  updateWeightMode: (preference, weight) =>
    dispatch(actions.updateWeightMode(preference, weight)),
  updateWeight: (preference, newScore, bidMode, prefIds) =>
    dispatch(actions.updateWeight(preference, newScore, bidMode, prefIds)),
  updateWeightAll: (preference, newScore, bidMode, prefIds) =>
    dispatch(actionsBidSession.default.updateWeightForAll(preference, newScore, bidMode, prefIds)),
  updateBidSession: bidSession => dispatch(updateBidSessionSuccess(bidSession)),
  copyCombinedPreference: (employeeBidId, groupName) =>
    dispatch(copyCombinedPreference(employeeBidId, groupName)),
  updateAllScores: preferences =>
      dispatch(actionsBidSession.default.updateAllScores(preferences)),
  updateOtherOption: (otherOptionCode, bidSessionId, staffId, value) =>
    dispatch(
      actionsBidSession.default.updateOtherOption(
        otherOptionCode,
        bidSessionId,
        staffId,
        value
      )
    ),
  fetchActivities: (staffId, startDate, endDate, bidSessionId, categoryId) =>
    dispatch(
      linearCalendarActions.default.fetchActivities(
        staffId,
        startDate,
        endDate,
        bidSessionId,
        categoryId
      )
    )
});

export default connect(mapStateToProps, mapDispatchToProps)(DefaultSettings);
